import { Drone, Viewer } from "@nova/schemas"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { WorkspaceFolderCard } from "./grid-view/WorkspaceFolderCard"
import { WorkspaceBodyGridSectionHeader } from "./grid-view/WorkspaceBodyGridSectionHeader"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectActiveWorkspaceId,
    selectSearchContents,
    setIdRenaming,
    updateTag,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useLocalStorageValue, setLocalStorageValue } from "tools/store/localStorageRegistry"
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"
import classNames from "classnames"
import { CreateFolder, Folder, Map, Plus } from "@nova/component-icons"
import { WorkspaceBodyEmptySection } from "./WorkspaceBodyEmptySection"
import { StickyHeaderRow } from "./list-view/sticky-header/StickyHeaderRow"
import { WorkspaceBodyListSectionHeader } from "./list-view/WorkspaceBodyListSectionHeader"
import { WorkspaceBodyNoSearchResults } from "./WorkspaceBodyNoSearchResults"
import {
    WorkspaceFolderProps,
    Folder as FolderType,
} from "features/data-management/workspace/types"
import { WorkspaceBodyFolderListItem } from "./list-view/WorkspaceBodyFolderListItem"
import { WorkspaceProjectCard } from "./grid-view/WorkspaceProjectCard"
import { WorkspaceBodyProjectListItem } from "./list-view/WorkspaceBodyProjectListItem"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import {
    ContextMenu,
    ContextMenuItem,
} from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { WorkspaceTreeMaxDepth } from "features/data-management/workspace/constants"
import { WorkspaceSpinner } from "features/data-management/workspace/WorkspaceSpinner"
import { setActiveContextMenu } from "features/popups/slice"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { useWorkspaceExplorerMode } from "panels/routing/WorkspaceExplorerRoutes"
import { BreadcrumbBar } from "../navigation-bar/breadcrumb-bar/BreadcrumbBar"
import { SortableDropdown } from "./grid-view/SortableDropdown"
import { ViewSwitcher } from "../navigation-bar/breadcrumb-bar/view-switcher/ViewSwitcher"
import * as Icons from "@nova/component-icons"
import {
    selectAllFoldersById,
    selectFavoritesData,
} from "features/data-management/workspace/context/selectors"
import { selectAllFires } from "features/entity-caching/fires/selectors"
import {
    toggleFavorite,
    moveWorkspaceEntity,
    deleteWorkspaceEntity,
    openWorkspaceShareModal,
} from "features/data-management/workspace/context/thunks"
import { useWorkspaceBodyContent } from "features/data-management/workspace/context/useWorkspaceBodyContent"
import { updateFire } from "features/entity-caching/fires/thunks"
import { listAllSubChains } from "features/data-management/workspace/module"
import {
    useActiveFolder,
    useCreateFolder,
    useCreateProject,
    useCreateTagChain,
} from "features/data-management/workspace/context/navigation"
import { ContentViewMode } from "@nova/constants"
import { useNavigationState } from "@nova/react-router"

export const gridItemClasses = "grid grid-cols-[repeat(auto-fill,minmax(15.625rem,1fr))] gap-5"

export const WorkspaceBodyProjectsAndFolders = () => {
    const activeworkspaceId = useAppSelector(selectActiveWorkspaceId()) || ""
    const dispatch = useAppDispatch()
    useNavigationState((state) => {
        if (state !== "promptInvite") return
        dispatch(openWorkspaceShareModal(activeworkspaceId))
    })

    const allFoldersById = useAppSelector(selectAllFoldersById)
    const allProjectsById = useAppSelector(selectAllFires)
    const { favoriteIds } = useAppSelector(selectFavoritesData)
    const isFavorite = (id: string) => favoriteIds.has(id)
    const activeFolder = useActiveFolder()
    const searchContents = useAppSelector(selectSearchContents())
    const { bodyFolderIds, bodyProjectIds } = useWorkspaceBodyContent(searchContents)
    const createFolder = useCreateFolder()
    const createProject = useCreateProject()
    const createTagChain = useCreateTagChain()

    const explorerMode = useWorkspaceExplorerMode()
    const isInFavorites = explorerMode === "favorites"
    const viewMode = useLocalStorageValue("contentViewMode")
    const { isEditor } = useWorkspacePermissions(activeFolder?.id)

    const members = useAppSelector(selectWorkspaceMembers)
    const isLoadingRecents = !members && explorerMode === "recents"

    const hasContent = (bodyFolderIds?.length || 0) > 0 || (bodyProjectIds?.length || 0) > 0

    const scrollRef = useRef<HTMLDivElement>(null)
    const [scrollbarIsShown, setScrollbarIsShown] = useState(false)
    useLayoutEffect(() => {
        if (!scrollRef.current) return
        setScrollbarIsShown(
            scrollRef.current && scrollRef.current?.scrollHeight > scrollRef.current?.clientHeight
        )
    }, [bodyFolderIds, bodyProjectIds, isLoadingRecents])

    const shouldShowGridMode = hasContent && viewMode === ContentViewMode.Grid
    const shouldShowListMode = hasContent && viewMode === ContentViewMode.List

    const activeWorkspaceBodyContextMenu = useAppSelector(selectActiveContextMenu("WorkspaceBody"))

    const contextMenuSections = useMemo(() => {
        const sections: ContextMenuItem[][] = []
        const id = activeFolder?.id
        if (isEditor) {
            const section = []
            section.push({ title: "New Project", Icon: Plus, onClick: () => createProject() })
            if ((createTagChain(id) ?? []).length <= WorkspaceTreeMaxDepth)
                section.push({
                    title: "New Folder",
                    Icon: CreateFolder,
                    onClick: () => createFolder({ insideOfId: id }),
                })
            sections.push(section)
        }
        return sections
    }, [activeFolder, isEditor, createFolder, createProject, createTagChain])

    const countChildren = useCallback(
        (folder: FolderType) => {
            const subChains = listAllSubChains(folder)
            let numberOfSubProjects = 0
            let mostRecentDate = folder.updatedAt ? new Date(folder.updatedAt).getTime() : 0
            const viewerByUserId: Record<string, Viewer> = {}
            const drones: Drone[] = []

            subChains.forEach((chain) => {
                const folderId = chain.at(-1)
                const projectIdsInThisFolder = (
                    allFoldersById[folderId || ""]?.projects.map(({ id }) => id) ?? []
                ).filter((id) => id in allProjectsById)
                numberOfSubProjects += projectIdsInThisFolder.length

                projectIdsInThisFolder.forEach((projectId) => {
                    const project = allProjectsById[projectId]
                    if (!project) return
                    const updatedAtDateString = project.updatedAt
                    if (updatedAtDateString) {
                        const updatedAt = new Date(updatedAtDateString).getTime()
                        if (updatedAt > mostRecentDate) mostRecentDate = updatedAt
                    }
                    project.viewers?.forEach((viewer) => {
                        viewerByUserId[viewer.userId] ??= { ...viewer, projectId: project.id }
                    })
                    if (project.drones) drones.push(...project.drones)
                })
            })

            return {
                numberOfSubFolders: subChains.length - 1,
                numberOfSubProjects,
                mostRecentChildUpdatedAt: new Date(mostRecentDate).toISOString(),
                viewers: Object.values(viewerByUserId),
                drones,
            }
        },
        [allFoldersById, allProjectsById]
    )

    return (
        <div
            className={classNames("flex flex-col w-full h-full text-gray-300 relative", {
                "gap-8": hasContent && viewMode === ContentViewMode.Grid,
                "gap-4": hasContent && viewMode === ContentViewMode.List,
            })}
            onContextMenu={(e) => {
                e.preventDefault()
                dispatch(
                    setActiveContextMenu({
                        type: "WorkspaceBody",
                        position: { top: e.clientY, left: e.clientX },
                    })
                )
            }}
        >
            <div className="flex justify-between items-center">
                <div>
                    <BreadcrumbBar />
                </div>
                <div className="flex items-center gap-4">
                    {viewMode === ContentViewMode.Grid && <SortableDropdown />}
                    <ViewSwitcher
                        firstOption={{
                            icon: Icons.Grid,
                            isSelected: viewMode === ContentViewMode.Grid,
                            onSwitch: () =>
                                setLocalStorageValue("contentViewMode", ContentViewMode.Grid),
                        }}
                        secondOption={{
                            icon: Icons.List,
                            isSelected: viewMode === ContentViewMode.List,
                            onSwitch: () =>
                                setLocalStorageValue("contentViewMode", ContentViewMode.List),
                        }}
                    />
                </div>
            </div>
            {activeWorkspaceBodyContextMenu && (
                <ContextMenu
                    sections={contextMenuSections}
                    onBlur={() => dispatch(setActiveContextMenu(undefined))}
                    isFixedPosition={true}
                    top={activeWorkspaceBodyContextMenu.position.top}
                    left={activeWorkspaceBodyContextMenu.position.left}
                />
            )}

            {hasContent && viewMode === ContentViewMode.List && <StickyHeaderRow />}
            <ScrollableArea ref={scrollRef} className="flex-grow">
                <div
                    className={classNames(
                        "flex flex-col flex-grow bg-center min-h-fit max-h-screen h-full pb-20",
                        {
                            "pr-2": !hasContent,
                            "pl-12": hasContent && shouldShowListMode,
                            "pr-11": hasContent && !scrollbarIsShown && shouldShowListMode,
                            "pr-18": hasContent && scrollbarIsShown && shouldShowListMode,
                            "gap-8": shouldShowGridMode,
                            "gap-4 -ml-2": shouldShowListMode,
                            "-mr-4": shouldShowListMode && !scrollbarIsShown,
                            "-mr-12": scrollbarIsShown && shouldShowGridMode,
                        }
                    )}
                >
                    {!isLoadingRecents ? (
                        hasContent ? (
                            <>
                                {bodyFolderIds.length > 0 && (
                                    <div>
                                        {viewMode === ContentViewMode.Grid ? (
                                            <WorkspaceBodyGridSectionHeader title="Folders" />
                                        ) : (
                                            <WorkspaceBodyListSectionHeader
                                                title="Folders"
                                                Icon={Folder}
                                            />
                                        )}
                                        <div
                                            className={
                                                viewMode === ContentViewMode.Grid
                                                    ? gridItemClasses
                                                    : undefined
                                            }
                                        >
                                            {bodyFolderIds.map((folderId, i) => {
                                                const folder = allFoldersById[folderId]
                                                if (!folder) return null

                                                const {
                                                    numberOfSubFolders,
                                                    numberOfSubProjects,
                                                    mostRecentChildUpdatedAt,
                                                    viewers,
                                                    drones,
                                                } = countChildren(folder)

                                                const props: WorkspaceFolderProps = {
                                                    folder,
                                                    isInFavorites,
                                                    tagChain: createTagChain(folder.id) ?? [],
                                                    numberOfSubFolders,
                                                    numberOfSubProjects,
                                                    mostRecentChildUpdatedAt,
                                                    isFavorite: isFavorite(folder.id),
                                                    viewers,
                                                    drones,
                                                    handleMove: (id, type, newParentFolderId) =>
                                                        dispatch(
                                                            moveWorkspaceEntity({
                                                                id,
                                                                type,
                                                                newParentFolderId,
                                                            })
                                                        ),
                                                    handleCreateFolder: (insideOfId) =>
                                                        createFolder({ insideOfId }),
                                                    handleBeginRenaming: (id) =>
                                                        dispatch(setIdRenaming(id)),
                                                    handleConfirmRenaming: (id, name, type) => {
                                                        if (type === "folder")
                                                            dispatch(
                                                                updateTag({ id, updates: { name } })
                                                            )
                                                        else
                                                            dispatch(
                                                                updateFire({
                                                                    id,
                                                                    updates: { name },
                                                                })
                                                            )
                                                        dispatch(setIdRenaming(undefined))
                                                    },
                                                    handleCancelRenaming: () =>
                                                        dispatch(setIdRenaming(undefined)),
                                                    handleOpenShareModal: (id) =>
                                                        dispatch(openWorkspaceShareModal(id)),
                                                    handleToggleFavorite: (id) =>
                                                        dispatch(toggleFavorite(id)).unwrap(),
                                                    handleDelete: (id, name, type) =>
                                                        dispatch(
                                                            deleteWorkspaceEntity({
                                                                id,
                                                                name,
                                                                type,
                                                            })
                                                        ),
                                                }

                                                return viewMode === ContentViewMode.Grid ? (
                                                    <WorkspaceFolderCard key={i} {...props} />
                                                ) : (
                                                    <WorkspaceBodyFolderListItem
                                                        key={i}
                                                        {...props}
                                                    />
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}

                                {bodyProjectIds.length > 0 && (
                                    <div>
                                        {viewMode === ContentViewMode.Grid ? (
                                            <WorkspaceBodyGridSectionHeader title="Projects" />
                                        ) : (
                                            <WorkspaceBodyListSectionHeader
                                                title="Projects"
                                                Icon={Map}
                                            />
                                        )}
                                        <div
                                            className={
                                                viewMode === ContentViewMode.Grid
                                                    ? gridItemClasses
                                                    : undefined
                                            }
                                        >
                                            {bodyProjectIds.map((projectId, i) => {
                                                const project = allProjectsById[projectId]
                                                if (!project) return null
                                                return viewMode === ContentViewMode.Grid ? (
                                                    <WorkspaceProjectCard
                                                        key={i}
                                                        project={project}
                                                        isInFavorites={isInFavorites}
                                                    />
                                                ) : (
                                                    <WorkspaceBodyProjectListItem
                                                        key={i}
                                                        project={project}
                                                        isInFavorites={isInFavorites}
                                                    />
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : searchContents ? (
                            <WorkspaceBodyNoSearchResults />
                        ) : (
                            <WorkspaceBodyEmptySection
                                handleCreateFolder={() =>
                                    createFolder({ insideOfId: activeFolder?.id })
                                }
                                handleCreateProject={createProject}
                                activeFolder={activeFolder}
                                createTagChain={(id) => createTagChain(id) ?? []}
                            />
                        )
                    ) : (
                        <WorkspaceSpinner />
                    )}
                </div>
            </ScrollableArea>
        </div>
    )
}
